/**
 * Installment List Container Component
 * Manages state and logic for installment selection
 */
import { useRouter } from 'next/navigation'
import React, { FC, useCallback, useMemo, useState } from 'react'

import { AdaptedVariant, buildPaymentUrl, CardModal } from '@/entities/Payments'

import { usePaymentModal } from '../../model/use-payment-modal-store'

import { InstallmentListPresentation } from './installment-list-presentation'

interface InstallmentSelectionProps {
	/** Available installment programs */
	installmentData: AdaptedVariant[]
	/** Current price for calculations */
	price: number
	/** Product ID */
	productId: number
	/** Product code */
	productKod: string
}

/**
 * Installment List Container
 * Manages state for bank selection, modal, and navigation
 */
export const InstallmentSelection: FC<InstallmentSelectionProps> = ({ installmentData, price, productId }) => {
	const router = useRouter()
	const { activeCondition, open, close, isOpen } = usePaymentModal()
	const [activeBank, setActiveBank] = useState(installmentData[0]?.id || '')

	// Memoized bank data
	const bankData = useMemo(() => installmentData.find(({ id }) => id === activeBank), [activeBank, installmentData])

	// Handle installment application
	const handleApplyInstallment = useCallback(
		(installmentCode: string, serviceId?: number) => {
			if (!bankData) return

			const url = buildPaymentUrl({
				productId,
				paymentId: bankData.id,
				installmentCode,
				serviceId
			})
			router.push(url)
		},
		[bankData, productId, router]
	)

	if (!installmentData.length || !bankData) {
		return null
	}

	return (
		<>
			<InstallmentListPresentation
				installmentData={installmentData}
				price={price}
				activeBank={activeBank}
				onBankChange={setActiveBank}
				onSpecialConditionModalOpen={open}
				onApplyInstallment={handleApplyInstallment}
				productId={productId}
			/>

			{/* Modal for special condition product selection */}
			{activeCondition && (
				<CardModal
					items={activeCondition.productList}
					onApply={serviceId => handleApplyInstallment(activeCondition.installmentCode, serviceId)}
					onClose={close}
					isOpen={isOpen}
				/>
			)}
		</>
	)
}
